import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const PropertyDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onNavigateNext,
  onComplete,
 CommonButtons, onBack
}) => {
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({});
  const [success, setSuccess] = useState("");
  const isInitialMount = useRef(true);

  // Field visibility states
  const [showApartmentName, setShowApartmentName] = useState(false);
  const [showPlotArea, setShowPlotArea] = useState(false);
  const [showLeaseYears, setShowLeaseYears] = useState(false);
  const [showTotalFloors, setShowTotalFloors] = useState(true);
  const [showNoOfFloors, setShowNoOfFloors] = useState(false);

  // Form state - all fields in lowercase
  const [form, setForm] = useState({
    propertyType: "",
    apartmentName: "",
    bhkType: "",
    ownershipType: "",
    builtUpArea: "",
    plotArea: "",
    leaseYears: "",
    floor: "",
    totalFloors: "",
    noOfFloors: "",
    propertyAge: "",
    facing: "",
    floorType: "",
    builtUpAreaUnit: "",
    plotAreaUnit: "",
  });

  const FIELD_TO_ATTRIBUTE_MAP = useMemo(() => ({
    propertyType: "resale_property_type",
    apartmentName: "resale_apartment_name", 
    bhkType: "resale_bhk_type", 
    ownershipType: "resale_ownership_type",
    leaseYears: "resale_lease_years", 
    builtUpArea: "resale_built_up_area", 
    plotArea: "resale_plot_area",
    propertyAge: "resale_property_age", 
    floorType: "resale_floor_type", 
    floor: "resale_floor",
    totalFloors: "resale_total_floor",
    noOfFloors: "resale_no_of_floors", 
    facing: "resale_facing",
    builtUpAreaUnit: "built_up_area_unit",
    plotAreaUnit: "plot_area_unit"
  }), []);

  // Validation rules
  const validationRules = useMemo(() => ({
    propertyType: { required: true, message: "Property type is required" },
    apartmentName: { required: true, message: "Apartment name is required" },
    bhkType: { required: true, message: "BHK type is required" },
    builtUpAreaUnit: { required: true, message: "Built-up area unit is required" },
    builtUpArea: {
      required: true,
      message: "Built-up area is required",
      custom: (value) => {
        if (value && Number(value) <= 0) return "Built-up area must be greater than 0";
        return null;
      },
    },
    propertyAge: { required: true, message: "Property age is required" },
  
    totalFloors: { required: true, message: "Total floors is required" },
    noOfFloors: { required: true, message: "No. of floors is required" },
    ownershipType: { required: true, message: "Ownership type is required" },
    plotArea: {
      required: true,
      message: "Plot area is required",
      custom: (value) => {
        if (value && Number(value) <= 0) return "Plot area must be greater than 0";
        return null;
      },
    },
    plotAreaUnit: {
      required: true,
      message: "Plot area unit is required",
    },
    leaseYears: {
      required: true,
      message: "Lease years is required",
      custom: (value) => {
        if (value && Number(value) <= 0) return "Lease years must be greater than 0";
        return null;
      },
    },
  }), []);

  // normalize
  const normalize = useCallback((s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""), []);

  // Get options for a field
  const getOptions = useCallback(
    (fieldKey) => {
      const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);
  
      let options = attributeValues.filter(
        (it) => it?.isActive && normalize(it.attrName) === match
      );
  
      // Custom Sorting for BHK / RK
      if (fieldKey === "bhkType") {
        options = [...options].sort((a, b) => {
          const parse = (label) => {
            const numMatch = label?.match(/\d+/);
            const number = numMatch ? Number(numMatch[0]) : 999;
  
            const isRK = label?.toLowerCase().includes("rk");
            const typePriority = isRK ? 0 : 1; 
  
            return { number, typePriority };
          };
         
  
          const A = parse(a.label);
          const B = parse(b.label);
  
          if (A.number !== B.number) {
            return A.number - B.number;
          }
  
          return A.typePriority - B.typePriority;
        });
      }
  
      if (fieldKey === "propertyAge") {
    const orderMap = {
      "less than": 1,
      "1 to 3": 2,
      "3 to 5": 3,
      "5 to 10": 4,
      "more than": 5,
    };
  
    options = [...options].sort((a, b) => {
      const getPriority = (label) => {
        const lower = label?.toLowerCase() || "";
  
        if (lower.includes("less than")) return orderMap["less than"];
        if (lower.includes("1 to 3")) return orderMap["1 to 3"];
        if (lower.includes("3 to 5")) return orderMap["3 to 5"];
        if (lower.includes("5 to 10")) return orderMap["5 to 10"];
        if (lower.includes("more than")) return orderMap["more than"];
  
        return 999; 
      };
  
      return getPriority(a.label) - getPriority(b.label);
    });
  }
      return options;
    },
    [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
  );

const updateFieldVisibility = useCallback(
  (propertyTypeIdOrLabel) => {
    const options = getOptions("propertyType");

    const selected =
      options.find((o) => String(o.id) === String(propertyTypeIdOrLabel)) ||
      options.find((o) => normalize(o.label) === normalize(propertyTypeIdOrLabel)) ||
      null;

    const optionName =
      selected?.label?.toLowerCase() ||
      String(propertyTypeIdOrLabel || "").toLowerCase();

    const isGatedCommunity = optionName.includes("gated community");
    const isVilla = optionName.includes("villa");
    const isIndependentHouse = optionName.includes("independent house");

   if (isGatedCommunity) {
  setShowApartmentName(true);
  setShowPlotArea(false);

  setShowTotalFloors(false);
  setShowNoOfFloors(true);

  setForm((prev) => ({
    ...prev,
    floor: "",
    totalFloors: ""
  }));

  return;
}

    const showPlot =
      (isVilla || isIndependentHouse) && !isGatedCommunity;

    setShowApartmentName(
      optionName.includes("apartment")
    );

    setShowPlotArea(showPlot);
    setShowTotalFloors(!showPlot);
    setShowNoOfFloors(showPlot);
  },
  [getOptions, normalize]
);

  const updateOwnershipVisibility = useCallback(
    (ownershipTypeIdOrLabel) => {
      const options = getOptions("ownershipType");
      const selected =
        options.find((o) => String(o.id) === String(ownershipTypeIdOrLabel)) ||
        options.find((o) => normalize(o.label) === normalize(ownershipTypeIdOrLabel)) ||
        null;
      const optionName =
        selected?.label?.toLowerCase() ||
        String(ownershipTypeIdOrLabel || "").toLowerCase();

      // Show "Lease Years" when the ownership label suggests a lease/rental
      // arrangement. Covers "Lease", "Leasehold", "On Lease", "Rented", etc.
      const isLeaseLike =
        optionName.includes("lease") ||
        optionName.includes("rented") ||
        optionName.includes("tenant");

      setShowLeaseYears(isLeaseLike);
    },
    [getOptions, normalize]
  );

  // Update form field
  const updateField = useCallback(
    (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setTouched((prev) => ({ ...prev, [key]: true }));

      if (key === "propertyType") updateFieldVisibility(value);
      if (key === "ownershipType") updateOwnershipVisibility(value);
    },
    [updateFieldVisibility, updateOwnershipVisibility]
  );

  const handleBlur = useCallback((fieldName) => setTouched((prev) => ({ ...prev, [fieldName]: true })), []);

  // Get field error
  const getFieldError = useCallback(
    (fieldName) => {
      const rule = validationRules[fieldName];
      if (!rule) return null;
      const val = form[fieldName];

      // Skip validation for hidden fields
      if (fieldName === "plotArea" && !showPlotArea) return null;
      if (fieldName === "apartmentName" && !showApartmentName) return null;
      if (fieldName === "leaseYears" && !showLeaseYears) return null;
      if (fieldName === "noOfFloors" && !showNoOfFloors) return null;
      if ((fieldName === "floor" || fieldName === "totalFloors") && showNoOfFloors) return null;

      if (rule.required) {
        if (val === "" || val === null || val === undefined) return rule.message;
      }
      if (rule.custom && val !== "" && val !== null && val !== undefined) {
  return rule.custom(val);
}
      return null;
    },
    [validationRules, form, showPlotArea, showApartmentName, showLeaseYears, showNoOfFloors]
  );
const hasFieldErrors = useCallback(() => {
  let requiredFields = [
    "propertyType",
    "bhkType",
    "builtUpArea",
     "builtUpAreaUnit",   
    "propertyAge",
    "facing",
    "floorType",
    "ownershipType"
  ];

  if (showNoOfFloors) {
    requiredFields.push("noOfFloors");
  } else {
    requiredFields.push("floor", "totalFloors");
  }

  if (showApartmentName) requiredFields.push("apartmentName");
  if (showPlotArea) requiredFields.push("plotArea");
  if (showLeaseYears) requiredFields.push("leaseYears");

  return requiredFields.some((f) => {
    const error = getFieldError(f);
    return !!error;
  });
}, [
  showNoOfFloors,
  showApartmentName,
  showPlotArea,
  showLeaseYears,
  getFieldError
]);
  
  useEffect(() => {
    
    if (!data || typeof data !== "object" || Object.keys(data).length === 0) return;

    const initializeForm = () => {
      const attributes = data.attributes || {};
      const areaInfo = data.area_information || {};
      const updated = {};

      const findValue = (obj, keys) => {
        for (const k of keys) {
          if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
        }
        return undefined;
      };

      Object.keys(FIELD_TO_ATTRIBUTE_MAP).forEach((fieldKey) => {
        const canonical = FIELD_TO_ATTRIBUTE_MAP[fieldKey];
        const variants = [
          canonical,
          canonical.toLowerCase(),
          canonical.toUpperCase(),
        ];

        let value = findValue(attributes, variants);
        if (value === undefined) value = findValue(areaInfo, variants);
        if (value === undefined) value = findValue(data, variants);

        if (value !== undefined && value !== null) {
          if (typeof value === "string" && String(Number(value)) === value) {
            updated[fieldKey] = isNaN(Number(value)) ? value : Number(value);
          } else if (typeof value === "number") {
            updated[fieldKey] = value;
          } else {
            const opts = getOptions(fieldKey);
            const matchedByLabel = Array.isArray(opts)
              ? opts.find((o) => 
                  normalize(o.label) === normalize(String(value)) || 
                  normalize(o.attr_val_name) === normalize(String(value))
                )
              : null;
            if (matchedByLabel) {
              updated[fieldKey] = matchedByLabel.id;
            } else {
              updated[fieldKey] = value;
            }
          }
        }
      });

      setForm((prev) => {
        const hasChanges = Object.keys(updated).some(
          key => JSON.stringify(prev[key]) !== JSON.stringify(updated[key])
        );
        
        if (!hasChanges && isInitialMount.current) {
          isInitialMount.current = false;
          return prev;
        }
        
        return { ...prev, ...updated };
      });

      // Set initial field visibility
const propVal = updated.propertyType;
if (propVal) {
  updateFieldVisibility(propVal);
}

const ownershipVal = updated.ownershipType;
if (ownershipVal) {
  updateOwnershipVisibility(ownershipVal);
}
      
      isInitialMount.current = false;
    };

    initializeForm();
  }, [data, getOptions, updateFieldVisibility, updateOwnershipVisibility, FIELD_TO_ATTRIBUTE_MAP, normalize]);

  

const handleSaveOnly = useCallback(async () => {
  const newTouched = {};
  Object.keys(form).forEach((k) => {
    newTouched[k] = true;
  });

  setTouched(newTouched);

  if (hasFieldErrors()) {
    setError("Please fill all required fields with valid data");
    return {
      success: false,
      error: "Validation failed",
    };
  }

  setSaving(true);
  setError("");
  setSuccess("");

  try {
    let propertyUuidToUse = propertyUuid;

    // ADD FLOW
    if (!propertyUuidToUse) {
      propertyUuidToUse = await createProperty(form);
      console.log("🆕 Resale property created:", {
        propertyUuidToUse,
      });
    } else {
      console.log("📝 Managing existing resale property:", {
        propertyUuidToUse,
      });
    }

    // Build attributes payload
    const attributesPayload = {};

     Object.keys(FIELD_TO_ATTRIBUTE_MAP).forEach((fieldKey) => {
      // Skip hidden fields — but only if the user didn't enter a value.
      // This prevents losing a value when visibility flags settle late.
      if (fieldKey === "plotArea" && !showPlotArea && !form.plotArea) return;
      if (fieldKey === "apartmentName" && !showApartmentName && !form.apartmentName) return;
      if (fieldKey === "leaseYears" && !showLeaseYears && !form.leaseYears) return;
      if (fieldKey === "noOfFloors" && !showNoOfFloors && !form.noOfFloors) return;
      if (
        (fieldKey === "floor" || fieldKey === "totalFloors") &&
        showNoOfFloors
      ) {
        return;
      }

      const val = form[fieldKey];

      if (val !== "" && val !== undefined && val !== null) {
        attributesPayload[FIELD_TO_ATTRIBUTE_MAP[fieldKey]] =
          isNaN(Number(val)) ? val : Number(val);
      }
    });

    // 1. Save property details
    const saveResult = await saveSectionAttributes(
      propertyUuidToUse,
      attributesPayload,
      form,
      "property_detail"
    );

    console.log("✅ RESALE property details saved:", saveResult);

    // 2. Get PROPERTY_ID from property_detail_attribute response
    const returnedPropertyId =
      saveResult?.response?.data?.property_id ||
      saveResult?.response?.data?.PROPERTY_ID ||
      null;

    console.log("✅ RESALE PROPERTY_ID:", returnedPropertyId);

    // 3. Notify parent ResaleForm
    onSaveSuccess?.(
      propertyUuidToUse,
      form,
      1,
      returnedPropertyId
    );

    setSuccess("Property details saved successfully!");

    if (onComplete) {
      onComplete(form);
    }

    return {
      success: true,
      data: {
        property_uuid: propertyUuidToUse,
        property_id: returnedPropertyId,
      },
      formData: form,
    };
  } catch (err) {
    console.error("Save failed:", err);

    const errorMessage =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to save property details";

    setError(errorMessage);

    return {
      success: false,
      error: errorMessage,
    };
  } finally {
    setSaving(false);
  }
}, [
  form,
  hasFieldErrors,
  propertyUuid,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  FIELD_TO_ATTRIBUTE_MAP,
  showPlotArea,
  showApartmentName,
  showLeaseYears,
  showNoOfFloors,
]);

  // Save and navigate to next
const handleSaveAndNext = useCallback(async (e) => {
  e?.preventDefault();

  const result = await handleSaveOnly();

  if (result?.success) {
    console.log("Save success navigating next");

    if (onNavigateNext) {
      onNavigateNext(result.formData);
    }
  }
}, [handleSaveOnly, onNavigateNext]);

  // Get CommonFields render functions
  const { renderDropdown, renderNumberField, renderTextInput } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999, minWidth: "300px" }}
          timeout={5000}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999, minWidth: "300px" }}
          timeout={3000}
        />
      )}

      <div style={{ backgroundColor: "white" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", backgroundColor: "white" }}>

              <div>
                <Stack>
                  {/* ROW 1: Property Type and Conditional Apartment Name */}
                  <FormGroup>
                    <Grid>
                      <Column sm={4} md={4} lg={8}>
                        {renderDropdown("propertyType", "Property Type")}
                      </Column>
                      <Column sm={4} md={4} lg={8}>
                        {showApartmentName && (
                          renderTextInput("apartmentName", "Apartment Name", "Enter apartment or community name", true)
                        )}
                      </Column>
                    </Grid>
                  </FormGroup>

                  {/* ROW 2: BHK Type and Conditional Plot Area */}
                  <FormGroup>
                    <Grid>
                      <Column sm={4} md={4} lg={8}>
                        {renderDropdown("bhkType", "BHK Type")}
                      </Column>
                      <Column sm={4} md={4} lg={8}>
                        {showPlotArea && (
                          <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                            <div style={{ flex: 2 }}>
                              {renderNumberField("plotArea", "Plot Area", "0", true)}
                            </div>
                            <div style={{ flex: 1 }}>
                              {renderDropdown("plotAreaUnit", "Unit", true)}
                            </div>
                          </div>
                        )}
                      </Column>
                    </Grid>
                  </FormGroup>

                  {/* ROW 3: Ownership Type and Conditional Lease Years */}
                  <FormGroup>
                    <Grid>
                      <Column sm={4} md={4} lg={8}>
                        {renderDropdown("ownershipType", "Ownership Type")}
                      </Column>
                      <Column sm={4} md={4} lg={8}>
                        {showLeaseYears && renderNumberField("leaseYears", "Lease Years", "0", true)}
                      </Column>
                    </Grid>
                  </FormGroup>

                  {/* ROW 4: Area Information */}
                  <FormGroup>
                    <Grid>
                      <Column sm={4} md={4} lg={8}>
                        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                          <div style={{ flex: 2 }}>
                            {renderNumberField(
                              "builtUpArea",
                              "Built-up Area",
                              "0",
                              true
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            {renderDropdown("builtUpAreaUnit", "Unit", true)}
                          </div>
                        </div>
                      </Column>

  
                    </Grid>
                  </FormGroup>

                  {/* ROW 5: Property Age and Facing */}
                  <FormGroup>
                    <Grid>
                      <Column sm={4} md={4} lg={8}>
                        {renderDropdown("propertyAge", "Property Age")}
                      </Column>
                      <Column sm={4} md={4} lg={8}>
                        {renderDropdown("facing", "Facing", false)}
                      </Column>
                    </Grid>
                  </FormGroup>

                  {/* ROW 6: Floor Information (Conditional) */}
                  <FormGroup style={{ marginBottom: "2rem" }}>
                    <Grid>
                      <Column sm={4} md={4} lg={5}>
                        {renderDropdown("floorType", "Floor Type", false)}
                      </Column>

                      {showNoOfFloors ? (
                        <Column sm={4} md={4} lg={5}>
                          {renderDropdown("noOfFloors", "No. of Floors")}
                        </Column>
                      ) : (
                        <>
                          <Column sm={4} md={4} lg={5}>
                            {renderDropdown("floor", "Floor")}
                          </Column>
                          <Column sm={4} md={4} lg={5}>
                            {showTotalFloors && renderDropdown("totalFloors", "Total Floors")}
                          </Column>
                        </>
                      )}
                    </Grid>
                  </FormGroup>

                  {/* CommonButtons from parent */}
                  <CommonButtons
                    onBack={onBack}
                    onSave={handleSaveOnly}
                    onNext={handleSaveAndNext}
                    showBack={true}
                    showSave={true}
                    showNext={true}
                    isSaveLoading={saving}
                  />
                </Stack>
              </div>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default PropertyDetails;